import agentGatewayClient from '@/services/agentGatewayClient'
import pccApi from '@/api/pcc'

const HEARTBEAT_TIMEOUT_MS = 60000
const STATUS_POLL_INTERVAL_MS = 15000

function hasFreshHeartbeat(agentStatus) {
  if (!agentStatus?.lastHeartbeatAt) return false
  return Date.now() - agentStatus.lastHeartbeatAt < HEARTBEAT_TIMEOUT_MS
}

let statusPollTimer = null
let agentChannelUnsubscribe = null

export default {
  state: {
    agentStatus: {
      online: false,
      lastHeartbeatAt: null,
      uptimeSecs: null,
      agentVersion: null,
      otavConnected: false
    },
    playlistState: null,
    lastPlaylistStateAt: null,
    schedulerState: null,
    routerState: null,
    routerLabels: { sources: [], destinations: [] },
    inFlightCommands: {},
    connectionState: 'disconnected',
    _stationId: null,
    platforms: [],
    platformsLoading: false,
    platformsError: null,
    debugChannelName: null,
    subscriptionState: 'idle',
    subscriptionError: null,
    authState: 'idle',
    authError: null
  },

  getters: {
    isAgentOnline(state) {
      return state.agentStatus.online && hasFreshHeartbeat(state.agentStatus)
    },
    isOtavConnected(state) {
      return state.agentStatus.online && state.agentStatus.otavConnected && hasFreshHeartbeat(state.agentStatus)
    },
    commandPending: (state) => (requestId) => {
      return !!state.inFlightCommands[requestId]
    }
  },

  mutations: {
    SET_PCC_STATION_ID(state, stationId) {
      state._stationId = stationId
    },
    SET_DEBUG_CHANNEL_NAME(state, channelName) {
      state.debugChannelName = channelName
    },
    SET_SUBSCRIPTION_STATE(state, subscriptionState) {
      state.subscriptionState = subscriptionState
    },
    SET_SUBSCRIPTION_ERROR(state, subscriptionError) {
      state.subscriptionError = subscriptionError
    },
    SET_AUTH_STATE(state, authState) {
      state.authState = authState
    },
    SET_AUTH_ERROR(state, authError) {
      state.authError = authError
    },
    SET_PLAYLIST_STATE(state, payload) {
      state.playlistState = payload
      state.lastPlaylistStateAt = Date.now()
    },
    SET_SCHEDULER_STATE(state, payload) {
      state.schedulerState = payload
    },
    SET_ROUTER_STATE(state, payload) {
      state.routerState = payload
    },
    SET_HEARTBEAT(state, payload) {
      const heartbeatTs = payload.last_heartbeat_at ? new Date(payload.last_heartbeat_at).getTime() : Date.now()
      state.agentStatus = {
        online: payload.status === 'online',
        lastHeartbeatAt: Number.isFinite(heartbeatTs) ? heartbeatTs : Date.now(),
        uptimeSecs: payload.uptime_secs ?? null,
        agentVersion: payload.agent_version ?? null,
        otavConnected: payload.status === 'online' ? (payload.otav_connected ?? false) : false
      }
    },
    SET_ROUTER_LABELS(state, payload) {
      state.routerLabels = payload
    },
    SET_CONNECTION_STATE(state, connectionState) {
      state.connectionState = connectionState
    },
    SET_IN_FLIGHT_COMMAND(state, { requestId, type, payload }) {
      state.inFlightCommands = { ...state.inFlightCommands, [requestId]: { type, payload, startedAt: Date.now() } }
    },
    RESOLVE_COMMAND(state, requestId) {
      const next = { ...state.inFlightCommands }
      delete next[requestId]
      state.inFlightCommands = next
    },
    SET_AGENT_OFFLINE(state) {
      state.agentStatus = { ...state.agentStatus, online: false }
    },
    SET_PLATFORMS(state, platforms) {
      state.platforms = platforms
    },
    SET_PLATFORMS_LOADING(state, loading) {
      state.platformsLoading = loading
    },
    SET_PLATFORMS_ERROR(state, error) {
      state.platformsError = error
    }
  },

  actions: {
    subscribeToAgentChannel({ commit, dispatch }, stationId) {
      if (!stationId) return

      if (typeof agentChannelUnsubscribe === 'function') {
        agentChannelUnsubscribe()
        agentChannelUnsubscribe = null
      }

      commit('SET_PCC_STATION_ID', stationId)
      commit('SET_DEBUG_CHANNEL_NAME', `private-cache-station-agent.${stationId}`)
      commit('SET_CONNECTION_STATE', 'connecting')
      commit('SET_SUBSCRIPTION_STATE', 'subscribing')
      commit('SET_SUBSCRIPTION_ERROR', null)
      commit('SET_AUTH_STATE', 'idle')
      commit('SET_AUTH_ERROR', null)

      // NewsDesk transport stores heartbeat status in API; poll as a fallback
      // so UI status stays accurate even if heartbeat is not delivered via WS.
      if (statusPollTimer) clearInterval(statusPollTimer)
      dispatch('syncAgentStatusFromApi', stationId)
      statusPollTimer = setInterval(() => {
        dispatch('syncAgentStatusFromApi', stationId)
      }, STATUS_POLL_INTERVAL_MS)

      agentChannelUnsubscribe = agentGatewayClient.subscribe(
        stationId,
        ({ type, payload, request_id }) => {
          dispatch('handleAgentMessage', { type, payload, request_id })
        },
        ({ state, error }) => {
          if (state === 'authorizing') {
            commit('SET_AUTH_STATE', error ? 'failed' : 'started')
            commit('SET_AUTH_ERROR', error ?? null)
            return
          }
          commit('SET_SUBSCRIPTION_STATE', state)
          commit('SET_SUBSCRIPTION_ERROR', error ?? null)
          if (state === 'subscribed') {
            commit('SET_CONNECTION_STATE', 'connected')
            commit('SET_AUTH_STATE', 'succeeded')
          }
        }
      )
    },

    unsubscribeFromAgentChannel({ commit }) {
      if (statusPollTimer) {
        clearInterval(statusPollTimer)
        statusPollTimer = null
      }

      if (typeof agentChannelUnsubscribe === 'function') {
        agentChannelUnsubscribe()
        agentChannelUnsubscribe = null
      }

      commit('SET_CONNECTION_STATE', 'disconnected')
      commit('SET_DEBUG_CHANNEL_NAME', null)
      commit('SET_SUBSCRIPTION_STATE', 'idle')
      commit('SET_SUBSCRIPTION_ERROR', null)
      commit('SET_AUTH_STATE', 'idle')
      commit('SET_AUTH_ERROR', null)
      commit('SET_AGENT_OFFLINE')
      commit('SET_PLATFORMS', [])
      commit('SET_PLATFORMS_ERROR', null)
    },

    async syncAgentStatusFromApi({ commit }, stationId) {
      if (!stationId) return
      try {
        const response = await pccApi.getAgentStatus(stationId)
        if (response?.data) {
          commit('SET_HEARTBEAT', response.data)
        }
      } catch {
        // Keep websocket-driven state if API polling is temporarily unavailable.
      }
    },

    handleAgentMessage({ commit }, { type, payload, request_id }) {
      switch (type) {
        case 'playlist_state':
          commit('SET_PLAYLIST_STATE', payload)
          break
        case 'scheduler_state':
          commit('SET_SCHEDULER_STATE', payload)
          break
        case 'router_state':
          commit('SET_ROUTER_STATE', payload)
          break
        case 'heartbeat':
          commit('SET_HEARTBEAT', payload)
          break
        case 'router_labels':
          commit('SET_ROUTER_LABELS', payload)
          break
        case 'command_response':
          if (request_id) commit('RESOLVE_COMMAND', request_id)
          break
        default:
          break
      }
    },

    async fetchPlatforms({ commit, state }, { force = false } = {}) {
      // Prevent concurrent in-flight fetches
      if (state.platformsLoading) return
      // Skip if already loaded successfully and not forced (e.g. station switch)
      if (!force && state.platforms.length && !state.platformsError) return
      commit('SET_PLATFORMS_LOADING', true)
      commit('SET_PLATFORMS_ERROR', null)
      try {
        // Active platforms only: PCC is an operational view, and a deactivated
        // platform has no rundown, feed or CMS to control. The detail page fetches
        // by id, so a direct link to an inactive platform still resolves.
        const resp = await pccApi.getPlatforms({ is_active: true })
        // index() paginates, so PlatformResource::collection() wraps in data/links/meta
        // regardless of JsonResource::withoutWrapping() (pagination metadata needs a home).
        commit('SET_PLATFORMS', resp.data?.data ?? [])
      } catch (err) {
        commit('SET_PLATFORMS_ERROR', err.message ?? 'Failed to load platforms')
        commit('SET_PLATFORMS', [])
      } finally {
        commit('SET_PLATFORMS_LOADING', false)
      }
    },

    dispatchCommand({ commit, state }, { type, payload = {} }) {
      const stationId = state._stationId
      return new Promise((resolve, reject) => {
        agentGatewayClient
          .dispatchCommand(stationId, type, payload)
          .then(({ request_id }) => {
            if (!request_id) {
              resolve({ success: true })
              return
            }
            commit('SET_IN_FLIGHT_COMMAND', { requestId: request_id, type, payload })
            agentGatewayClient.onCommandResponse(request_id, (response) => {
              commit('RESOLVE_COMMAND', request_id)
              resolve(response)
            })
          })
          .catch(reject)
      })
    }
  }
}
